import type { Agent, Flight, FlightLeg, QuotePassenger } from '../types';
import { airlineLogoUrl } from './airlineLogo';
import { airportCityLabel } from './airportLabels';
import { buildQuoteEmailBannerUrl } from './hosts';
import { routeSummaryLabel } from './roundTrip';
import { escapeHtml, wrapSkyluxClientMailDocument } from './skyluxClientMailLayouts';

const LOGO_DARK = 'https://www.skyluxtravel.com/static/img/header/logo-dark-new.png';
const PHONE_ICON = 'https://attachment-storage.skyluxtravel.com/16/300/397759cfaff186a7f0d647ce4065b31e.png';
const DEFAULT_AVATAR = 'https://agent-photos.skyluxtravel.com/51868_employee_photo';
const COMPANY_PHONE = '888-859-6413';
const COMPANY_PHONE_DISPLAY = '888 - 859 - 6413';
const GENERAL_LINE = '(855) 507-9770';

const ICON_OUTBOUND = 'https://attachment-storage.skyluxtravel.com/16/300/c0674d6184f668ee30fc7b6244b4a591.png';
const ICON_RETURN = 'https://attachment-storage.skyluxtravel.com/16/300/2cc769c865149704f59d075403e8306c.png';
const ICON_STOPS = 'https://attachment-storage.skyluxtravel.com/16/300/05338594a7d2a4faab3b8d3d4c98baf5.png';
const AFFIRM_BG = 'https://attachment-storage.skyluxtravel.com/16/300/213178f47fd7e40ee9358b2f698aa3e0.png';
const AFFIRM_LOGO = 'https://attachment-storage.skyluxtravel.com/16/300/f964a771c0b8e95019e9eb9cec3997e9.png';
const DISCLAIMER_ICON = 'https://attachment-storage.skyluxtravel.com/16/300/df29bdf5729f094b7d5c176196c98dc3.png';
const FOOTER_DIVIDER = 'https://attachment-storage.skyluxtravel.com/16/300/2b3b54cf8b92c7841834d2d7b0ae89f2.png';

export type SkyluxQuoteClientMailParams = {
  clientName: string;
  introText: string;
  flight: Flight;
  pricePerPerson: number;
  passengerCount: number;
  bookingUrl: string;
  paymentUrl: string;
  quoteId: string;
  agent: Pick<Agent, 'name' | 'nickname' | 'email' | 'avatar'>;
  passengers?: QuotePassenger[];
  /** Flight card CTA — booking page (default) or OAF payment checkout. */
  primaryCta?: 'booking' | 'payment';
};

function buildQuotePassengersHtml(passengers: QuotePassenger[]): string {
  const list = passengers.filter((p) => p.name.trim());
  if (list.length === 0) return '';
  const rows = list
    .map((p, i) => {
      const dob = p.dateOfBirth ? escapeHtml(p.dateOfBirth) : '—';
      const age = p.age != null ? escapeHtml(String(p.age)) : '—';
      return `<tr>
<td style="padding:8px 12px;font-family:Arial;font-size:13px;color:rgb(0,0,0);border-bottom:1px solid rgb(229,229,229)">${i + 1}. ${escapeHtml(p.name.toUpperCase())}</td>
<td style="padding:8px 12px;font-family:Arial;font-size:13px;color:rgb(0,0,0);border-bottom:1px solid rgb(229,229,229)">${age}</td>
<td style="padding:8px 12px;font-family:Arial;font-size:13px;color:rgb(0,0,0);border-bottom:1px solid rgb(229,229,229)">${dob}</td>
</tr>`;
    })
    .join('');
  return `<table style="padding:0 10px 16px" width="100%"><tbody><tr><td>
<p style="font-family:'PT Serif',Georgia,serif;font-size:18px;font-weight:bold;line-height:18px;margin:0 0 10px;color:rgb(0,0,0)">Passengers on file</p>
<table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid rgb(229,229,229);border-radius:3px;background:#fff">
<thead><tr>
<th style="padding:8px 12px;text-align:left;font-family:Arial;font-size:11px;color:rgb(132,135,141);border-bottom:1px solid rgb(229,229,229)">Name</th>
<th style="padding:8px 12px;text-align:left;font-family:Arial;font-size:11px;color:rgb(132,135,141);border-bottom:1px solid rgb(229,229,229)">Age</th>
<th style="padding:8px 12px;text-align:left;font-family:Arial;font-size:11px;color:rgb(132,135,141);border-bottom:1px solid rgb(229,229,229)">Date of birth</th>
</tr></thead>
<tbody>${rows}</tbody>
</table>
</td></tr></tbody></table>`;
}

function formatLegDateShort(travelDate?: string): string {
  const d = travelDate?.trim();
  if (!d) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return d;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: '2-digit' });
}

function formatTime12h(time?: string): string {
  const t = time?.trim();
  if (!t) return '—';
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return t;
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${min} ${ampm}`;
}

function formatDurationMinutes(mins?: number): string {
  if (mins == null || !Number.isFinite(mins) || mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}Hr ${m}m`;
  if (h) return `${h}Hr`;
  return `${m}m`;
}

function stopsLabel(stops?: number): string {
  if (stops == null || stops <= 0) return 'Nonstop';
  if (stops === 1) return '1 stop';
  return `${stops} stops`;
}

/** Outbound city pair for quote copy (departure → arrival of the quoted leg). */
function quoteRouteEndpoints(f: Flight): { from: string; to: string } {
  return {
    from: airportCityLabel(f.departure),
    to: airportCityLabel(f.arrival),
  };
}

function clientGreetingName(clientName: string): string {
  const name = clientName.trim();
  return name || 'Traveler';
}

function routeTitle(f: Flight): string {
  const { from, to } = quoteRouteEndpoints(f);
  return `${from} - ${to}`;
}

function formatMailSubjectPrice(price: number): string {
  const amount = Number.isFinite(price) ? price : 0;
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function mailSubjectDestinationLabel(destination: string): string {
  const code = destination.trim();
  if (!code) return 'Destination';
  return airportCityLabel(code) || code;
}

/** Subject for branded quote emails. */
export function buildQuoteEmailSubject(destination: string, pricePerPerson: number): string {
  const dest = mailSubjectDestinationLabel(destination);
  return `SkyLux Travel: Quote - Flight to ${dest} - $${formatMailSubjectPrice(pricePerPerson)}`;
}

/** Subject for payment / OAF link emails. */
export function buildPaymentEmailSubject(destination: string, pricePerPerson: number): string {
  const dest = mailSubjectDestinationLabel(destination);
  return `SkyLux Travel: Payment Link - Flight to ${dest} - $${formatMailSubjectPrice(pricePerPerson)}`;
}

export type SkyluxQuoteEmailIntroParams = {
  clientName: string;
  agent: Pick<Agent, 'name' | 'nickname'>;
  flight: Flight;
  primaryCta?: 'booking' | 'payment';
};

/** Plain-text opening for quote / OAF client emails. */
export function buildSkyluxQuoteEmailIntroPlain(p: SkyluxQuoteEmailIntroParams): string {
  const primaryCta = p.primaryCta ?? 'booking';
  const client = clientGreetingName(p.clientName);
  const agentName = p.agent.name.trim() || agentDisplayShort(p.agent);
  const { from, to } = quoteRouteEndpoints(p.flight);

  if (primaryCta === 'payment') {
    return [
      `Dear ${client},`,
      `My name is ${agentName}, I'm your personal travel expert. Below you will find information on the itinerary from "${from}" to "${to}" customized specially for your needs.`,
      `Please feel free to contact me any time with your questions. To complete the purchase of your flight option, click button "Pay Now" below.`,
      `${from} - ${to}`,
    ].join('\n');
  }

  return [
    `Dear ${client},`,
    'Thank you for choosing Skylux Travel. Your personalized quote is ready.',
    '',
    'Thank you for choosing SkyLux Travel.',
    "Below you will find the ticket options currently available per your request. Please don't hesitate to contact me if any questions arise.",
    'To see the full details of the provided flight options, please click the "View deal" button below.',
    '',
    'To complete payment, pay here.',
  ].join('\n');
}

function buildQuoteIntroHtml(p: SkyluxQuoteEmailIntroParams): string {
  const primaryCta = p.primaryCta ?? 'booking';
  const client = escapeHtml(clientGreetingName(p.clientName));
  const agentName = escapeHtml(p.agent.name.trim() || agentDisplayShort(p.agent));
  const { from, to } = quoteRouteEndpoints(p.flight);
  const fromEsc = escapeHtml(from);
  const toEsc = escapeHtml(to);
  if (primaryCta === 'payment') {
    const routeLine = `${fromEsc} - ${toEsc}`;
    return `<span style="font-family:'PT Serif',Georgia,serif;font-size:20px;font-weight:bold;line-height:24px;color:rgb(0,0,0)">Dear ${client},</span><br>
My name is ${agentName}, I&apos;m your personal travel expert. Below you will find information on the itinerary from &quot;${fromEsc}&quot; to &quot;${toEsc}&quot; customized specially for your needs.<br>
Please feel free to contact me any time with your questions. To complete the purchase of your flight option, click button <strong style="font-family:Arial;color:rgb(0,0,0)">&quot;Pay Now&quot;</strong> below.<br>
${routeLine}`;
  }

  return `<span style="font-family:'PT Serif',Georgia,serif;font-size:20px;font-weight:bold;line-height:24px;color:rgb(0,0,0)">Dear ${client},</span><br><br>
Thank you for choosing Skylux Travel. Your personalized quote is ready.<br><br>
Thank you for choosing SkyLux Travel.<br>
Below you will find the ticket options currently available per your request. Please don&apos;t hesitate to contact me if any questions arise.<br>
To see the full details of the provided flight options, please click the <strong style="font-family:Arial;color:rgb(0,0,0)">&quot;View deal&quot;</strong> button below.<br><br>
To complete payment, pay here.`;
}

function agentDisplayShort(agent: Pick<Agent, 'name' | 'nickname'>): string {
  const nick = agent.nickname?.trim();
  if (nick) return nick;
  const parts = agent.name.trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0]} ${parts[1]![0]!.toUpperCase()}.`;
  return parts[0] ?? 'Your agent';
}

function buildLegSegmentHtml(leg: FlightLeg, legIcon: string): string {
  const dateLabel = escapeHtml(formatLegDateShort(leg.travelDate));
  const depTime = escapeHtml(formatTime12h(leg.departureTime));
  const arrTime = escapeHtml(formatTime12h(leg.arrivalTime));
  const depPlace = escapeHtml(`${leg.departure}`);
  const arrPlace = escapeHtml(`${leg.arrival}`);
  const travelTime = escapeHtml(formatDurationMinutes(leg.totalDuration));
  const stops = escapeHtml(stopsLabel(leg.stopsCount));

  return `<table style="width:100%">
<thead>
<tr>
<td style="padding:16px 5px 8px 17px;font-size:14px;line-height:16px;text-align:left">
<img src="${legIcon}" alt="" style="margin-right:10px" width="15">
<span style="font-family:Arial;font-size:18px;font-weight:400;line-height:16px;color:rgb(0,0,0)">${dateLabel}</span>
</td>
<td style="font-family:Arial;padding:12px 10px 8px;font-size:11px;font-weight:400;line-height:12px;text-align:right;color:rgb(132,135,141)">
Travel time:<br>${travelTime}
</td>
</tr>
</thead>
<tbody>
<tr>
<td colspan="2">
<table style="width:100%">
<thead>
<tr>
<th style="width:40%;min-width:40%;max-width:40%;padding:5px 0 0 16px;vertical-align:top;text-align:left">
<span style="font-family:Arial;font-size:16px;font-weight:400;text-transform:uppercase;line-height:16px;color:rgb(0,0,0)">${depTime}</span>
</th>
<th style="width:40%;max-width:40%;padding:5px 0 0 16px;vertical-align:top;text-align:left">
<span style="font-family:Arial;font-size:16px;font-weight:400;text-transform:uppercase;line-height:16px;color:rgb(0,0,0)">${arrTime}</span>
</th>
</tr>
</thead>
<tbody>
<tr style="line-height:2px">
<td style="width:20px;text-align:right">
<img src="${ICON_STOPS}" alt="" style="max-width:50px">
</td>
<td width="auto" style="font-family:Arial;min-width:40px;font-size:10px;text-align:right;padding-right:10px">${stops}</td>
</tr>
<tr>
<td style="padding:0 0 0 16px;vertical-align:text-top;width:40%;max-width:40%;text-align:left">
<span style="font-family:Arial;font-size:14px;line-height:16px;display:block;color:rgb(132,135,141)">${depPlace}</span>
</td>
<td style="padding:0 0 0 16px;vertical-align:text-top;width:40%;max-width:40%;text-align:left">
<span style="font-family:Arial;font-size:14px;line-height:16px;display:block;color:rgb(132,135,141)">${arrPlace}</span>
</td>
</tr>
</tbody>
</table>
</td>
</tr>
<tr><td><br></td></tr>
</tbody>
</table>`;
}

function buildFlightOptionCardHtml(
  f: Flight,
  optionIndex: number,
  ctaUrl: string,
  ctaLabel: string,
  totalUsd: number,
  passengerCount: number,
): string {
  const airline = escapeHtml(f.airline);
  const logo = airlineLogoUrl(f.flightNumber);
  const logoImg = logo
    ? `<img style="max-height:50px;max-width:100%;vertical-align:middle;display:inline-block;margin:auto;padding:6px 12px" src="${escapeHtml(logo)}" alt="${airline}">`
    : `<span style="font-family:Arial;font-size:14px;font-weight:700;color:rgb(0,0,0)">${airline}</span>`;
  const legsHtml = [
    buildLegSegmentHtml(f, ICON_OUTBOUND),
    ...(f.returnLeg ? [buildLegSegmentHtml(f.returnLeg, ICON_RETURN)] : []),
  ].join('');
  const priceLabel = passengerCount > 1 ? `Regular ticket price (${passengerCount} adults)` : 'Regular ticket price (adult)';
  const priceStr = escapeHtml(`${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`);
  const dealUrl = escapeHtml(ctaUrl);
  const ctaText = escapeHtml(ctaLabel);

  return `<table align="center" border="0" cellpadding="0" cellspacing="0" style="margin:auto;background-image:none;background-position:0% 0%;background-size:auto;background-repeat:repeat;background-origin:padding-box;background-clip:border-box;border-width:1px 1px 5px;border-style:solid;border-radius:3px;border-color:rgb(225,225,225) rgb(225,225,225) rgb(226,226,226)" width="100%">
<thead>
<tr>
<th style="padding:12px 12px 12px 17px;line-height:25px;text-align:left;width:60%;background-color:rgb(247,247,247)">
<span style="font-family:'PT Serif',Georgia,serif;text-transform:uppercase;font-size:18px;font-weight:bold;color:rgb(0,0,0)">${optionIndex}. ${airline}</span>
</th>
<td style="font-size:16px;font-weight:bold;padding-right:16px;line-height:25px;text-align:right;width:40%;background-color:rgb(247,247,247)">
<div style="width:100%;display:inline-block;padding:0 5px 17px">
<div style="display:inline-block;height:50px;width:105px;vertical-align:middle;text-align:center">${logoImg}</div>
</div>
</td>
</tr>
</thead>
<tbody>
<tr>
<td style="text-align:center;width:100%" colspan="2">
<div style="width:100%">
<table style="width:100%;text-align:left;border-right:1px dotted rgb(237,237,237);min-height:160px" border="0" cellpadding="0" cellspacing="0">
<tbody><tr><td>${legsHtml}</td></tr></tbody>
</table>
</div>
</td>
</tr>
<tr>
<td colspan="2">
<div style="margin:0 20px 20px">
<div style="background:0% 0% repeat rgb(239,247,255);font-size:0">
<div style="display:inline-block;width:60%;vertical-align:top">
<div style="padding:13px">
<div style="height:10px;font-family:Arial;font-size:9px;line-height:1;color:rgb(0,0,0)">${escapeHtml(priceLabel)}</div>
<div style="height:20px"><strong style="font-family:Arial;font-size:17px;line-height:1;margin-right:10px;color:rgb(0,0,0)">${priceStr}</strong></div>
</div>
</div>
<div style="display:inline-block;width:40%;text-align:right">
<div style="padding:11px 13px">
<a href="${dealUrl}" style="display:inline-block;text-align:center;font-size:14px;line-height:1;text-decoration:none;border:1px solid rgb(240,204,127);padding:9px 18px;background-color:rgb(240,204,127);color:rgb(0,0,0)" rel="noopener" target="_blank">
<strong style="font-family:Arial;text-transform:uppercase">${ctaText}</strong> ➞
</a>
</div>
</div>
</div>
</div>
</td>
</tr>
</tbody>
</table>`;
}

/** Client quote email — layout from `aditional/quote.html`. */
export function buildSkyluxQuoteClientMailHtml(p: SkyluxQuoteClientMailParams): string {
  const primaryCta = p.primaryCta ?? 'booking';
  const introHtml = buildQuoteIntroHtml({
    clientName: p.clientName,
    agent: p.agent,
    flight: p.flight,
    primaryCta,
  });
  const agentShort = escapeHtml(agentDisplayShort(p.agent));
  const agentEmail = escapeHtml(p.agent.email.trim());
  const avatar = escapeHtml(p.agent.avatar?.trim() || DEFAULT_AVATAR);
  const route = escapeHtml(routeTitle(p.flight));
  const totalUsd = p.pricePerPerson * Math.max(1, p.passengerCount);
  const bookingUrl = p.bookingUrl.trim();
  const paymentUrl = p.paymentUrl.trim();
  const ctaUrl = primaryCta === 'payment' ? paymentUrl : bookingUrl;
  const ctaLabel = primaryCta === 'payment' ? 'Pay Now' : 'View Deal';
  const passengersHtml =
    primaryCta === 'payment' && p.passengers?.length ? buildQuotePassengersHtml(p.passengers) : '';

  const body = `<div>
<table border="0" cellpadding="0" cellspacing="0" style="width:100%;table-layout:fixed"><tbody><tr><td style="width:100%">
<div style="min-width:300px;max-width:600px;margin:0 auto">
<table align="center" border="0" cellpadding="0" cellspacing="0" style="width:100%"><tbody><tr>
<td style="width:100%;padding:15px 10px 15px 20px" width="100%">
<table align="center" border="0" cellpadding="0" cellspacing="0" style="margin:auto" width="100%"><tbody><tr>
<td align="left" width="43%" style="vertical-align:middle">
<img src="${LOGO_DARK}" width="86" height="16" alt="SkyLux Travel">
</td>
<td align="right">
<table style="width:100%"><tbody><tr>
<td style="text-align:right">
<div style="font-size:12px;line-height:16px;font-family:Arial;color:rgb(0,0,0)">${agentShort}</div>
<div style="font-size:14px;font-family:Arial;color:rgb(0,0,0)">
<a style="text-decoration:none;font-weight:bold;font-size:14px;font-family:Arial;color:rgb(0,0,0)" href="tel:+1${COMPANY_PHONE.replace(/\D/g, '')}" target="_blank" rel="noopener">
<img style="margin-right:8px;height:10px;width:10px;font-family:Arial" src="${PHONE_ICON}" alt="">${COMPANY_PHONE_DISPLAY}
</a>
</div>
</td>
<td style="text-align:right;width:33px">
<div style="width:33px;height:33px"><img style="border-radius:30px" width="33" height="33" alt="" src="${avatar}"></div>
</td>
</tr></tbody></table>
</td>
</tr></tbody></table>
</td></tr></tbody></table>
</div>
</td></tr></tbody></table>

<table border="0" cellpadding="0" cellspacing="0" style="width:100%;table-layout:fixed;border-top:1px solid rgb(235,235,235);background-color:rgb(237,237,237)"><tbody><tr><td style="width:100%">
<div style="min-width:300px;max-width:600px;margin:0 auto">
<table style="padding:15px 20px" width="100%"><tbody><tr>
<td style="font-size:18px;padding-left:10px;line-height:22px;text-align:left">
<p style="font-size:13px;line-height:18px;margin:16px 0 15px;font-family:Arial;color:rgb(0,0,0)">
${introHtml}
</p>
</td></tr></tbody></table>

${buildWhyUsBlockHtml()}



${buildCryptoAcceptsBannerHtml()}

<div style="padding:20px 10px">
<table style="padding:15px 20px" width="100%"><tbody><tr>
<td style="text-align:center"><span style="font-family:'PT Serif',Georgia,serif;font-size:20px;font-weight:bold;line-height:18px;color:rgb(0,0,0)">${route}</span></td>
</tr></tbody></table>

${passengersHtml}

${buildFlightOptionCardHtml(p.flight, 1, ctaUrl, ctaLabel, totalUsd, p.passengerCount)}

<div style="width:100%;max-height:70px;margin-top:10px">
<div style="border-width:1px 1px 5px;border-style:solid;padding:15px 8px;border-color:rgb(226,226,226)">
<table style="width:100%"><tbody><tr>
<td style="width:5%;text-align:right"><img src="${DISCLAIMER_ICON}" style="width:18px;height:18px;padding-right:5px" alt=""></td>
<td style="width:95%"><span style="font-family:Arial;font-size:15px;font-weight:400;line-height:20px;color:rgb(132,135,141)">
The quoted prices are not guaranteed until the tickets are issued.
</span></td>
</tr></tbody></table>
</div>
</div>

<table style="padding:0 10px 10px" width="100%"><tbody><tr>
<td><img alt="" width="100%" src="${FOOTER_DIVIDER}"></td>
</tr></tbody></table>

<table style="padding:5px 20px" width="100%"><tbody><tr>
<td style="font-size:18px;line-height:22px;text-align:center">
<div style="margin-top:10px"><span style="font-family:'PT Serif',Georgia,serif;font-size:20px;font-weight:bold;line-height:16px;color:rgb(0,0,0)">Sincerely, ${agentShort}</span></div>
<p style="font-size:13px;line-height:18px;margin-bottom:14px;font-family:Arial;color:rgb(0,0,0)">
If you have additional questions or requests concerning your trip, you can contact me by phone, or simply replying to this email.
</p>
</td></tr></tbody></table>

${buildAgentContactFooterHtml(agentEmail)}

</div>
</td></tr></tbody></table>

<table border="0" cellpadding="0" cellspacing="0" style="width:100%;table-layout:fixed;background-color:rgb(55,50,50)"><tbody><tr><td style="width:100%">
<div style="min-width:300px;max-width:600px;margin:0 auto">
<div style="text-align:center;padding:32px 18px">
<div style="padding:4px 0"><span style="font-family:Arial;font-size:12px;font-weight:400;line-height:16px;color:rgb(255,255,255)">919 N. Market St., Ste 725, Wilmington, DE 19801</span></div>
<div style="font-family:Arial;padding:4px 0">
<a style="font-size:12px;line-height:16px;text-decoration:none;font-family:Arial;color:rgb(240,204,127)" href="https://www.skyluxtravel.com/contacts" target="_blank" rel="noopener">Customer Service</a>
<em style="font-family:Arial;font-size:12px;line-height:16px;font-style:normal;margin:0 4px;color:rgb(240,204,127)">|</em>
<a style="font-size:12px;line-height:16px;text-decoration:none;font-family:Arial;color:rgb(240,204,127)" href="https://www.skyluxtravel.com/terms" target="_blank" rel="noopener">Terms &amp; Conditions</a>
<em style="font-family:Arial;font-size:12px;line-height:16px;font-style:normal;margin:0 4px;color:rgb(240,204,127)">|</em>
<a style="font-size:12px;line-height:16px;text-decoration:none;font-family:Arial;color:rgb(240,204,127)" href="https://www.skyluxtravel.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>
</div>
</div>
</div>
</td></tr></tbody></table>
</div>`;

  const aria = `SkyLux Travel quote ${routeTitle(p.flight)}. Quote ${p.quoteId}.`;
  const documentTitle = primaryCta === 'payment' ? 'SkyLux Travel — Payment' : 'SkyLux Travel — Ticket quote';
  return wrapSkyluxClientMailDocument(body, aria, documentTitle);
}

function buildCryptoAcceptsBannerHtml(): string {
  const bannerUrl = escapeHtml(buildQuoteEmailBannerUrl());
  return [
    '<div style="padding:0 10px 12px">',
    '<table border="0" cellpadding="0" cellspacing="0" width="100%" align="center" style="max-width:600px;margin:0 auto">',
    '<tbody><tr><td align="center" style="padding:0">',
    `<img src="${bannerUrl}" alt="SkyLux Travel Accepts Crypto" width="580" style="display:block;width:100%;max-width:580px;height:auto;border:0;outline:none;line-height:0;font-size:0;border-radius:3px" />`,
    '</td></tr></tbody></table>',
    '</div>',
  ].join('');
}

function buildWhyUsBlockHtml(): string {
  const row = (icon: string, text: string) =>
    `<tr><td valign="middle" align="left" height="40" width="50" style="max-width:50px"><img src="${icon}" alt=""></td><td valign="middle" align="left" style="font-family:Arial">${escapeHtml(text)}</td></tr>`;
  const col = (rows: string) =>
    `<div style="width:100%;max-width:250px;display:inline-block;vertical-align:top;padding:0"><table border="0" cellpadding="0" cellspacing="0" width="100%"><tbody><tr><td style="padding:0"><table border="0" cellpadding="0" cellspacing="0" width="100%"><tbody><tr><td align="center" style="padding:0"><table border="0" cellpadding="0" cellspacing="0" width="100%" style="font-size:13px;font-weight:400;line-height:18px;margin-top:10px;margin-bottom:10px;font-family:Arial;color:rgb(50,44,49)"><tbody>${rows}</tbody></table></td></tr></tbody></table></td></tr></tbody></table></div>`;

  return `<div style="padding:10px;font-family:arial,sans-serif">
<div style="max-width:600px;margin:0 auto;border-width:1px 1px 5px;border-style:solid;font-family:arial,sans-serif;border-color:rgb(225,225,225) rgb(225,225,225) rgb(226,226,226)">
<table align="center" style="border-spacing:0;margin:0 auto;width:100%;max-width:600px;font-family:arial,sans-serif"><tbody><tr><td style="padding:0">
<div style="height:36px;min-height:36px;padding-top:19px;width:100%;font-size:13px;font-weight:400;font-family:arial,sans-serif;background-color:rgb(247,247,247);color:white">
<span style="font-family:Arial;margin-left:20px;vertical-align:middle;font-weight:bold;color:rgb(0,0,0)">Why Us?</span>
</div>
<table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width:560px;margin:0 auto;font-family:Arial"><tbody><tr>
<td style="text-align:center;font-size:0;padding:0">
${col(
  row('https://attachment-storage.skyluxtravel.com/16/300/ea5a976b07c5c8ef3c1a9ff47e4014b8.png', '500+ Travel Managers 24/7') +
    row('https://attachment-storage.skyluxtravel.com/16/300/c48c33fca043e3f40d9a5a3854332fd6.png', 'Skylux Price Guarantee') +
    row('https://attachment-storage.skyluxtravel.com/16/300/4e696f1ba4d326380ecdf2b3ec4a68bc.png', 'TOP rated agency in TrustPilot with over 23K reviews'),
)}
${col(
  row('https://attachment-storage.skyluxtravel.com/16/300/dbb388118370e298469ae8864597d230.png', 'BBB A+ Rating') +
    row('https://attachment-storage.skyluxtravel.com/16/300/c8aa5c15dd22972e3d372db55eb553ff.png', 'Referral Program') +
    row('https://attachment-storage.skyluxtravel.com/16/300/b7f60dd90c7d265349ee4dbc9709260d.png', '11 years of excellence: Always imitated, Never duplicated!'),
)}
</td></tr></tbody></table>
</td></tr></tbody></table>
</div></div>`;
}

function buildAgentContactFooterHtml(agentEmail: string): string {
  return `<table style="padding:15px 20px" width="100%"><tbody><tr>
<td style="font-size:18px;line-height:22px;text-align:center;padding:8px 0 0">
<div style="display:inline-block;width:100%;max-width:240px;float:left;padding:10px;margin-bottom:8px">
<div style="line-height:20px;font-weight:400;font-family:Arial;font-size:13px">Direct Phone Line:</div>
<div style="line-height:20px;font-weight:bold;font-size:15px">
<a style="text-decoration:none;font-family:Arial;color:rgb(0,0,0)" href="tel:+1${COMPANY_PHONE.replace(/\D/g, '')}" target="_blank" rel="noopener">
<img style="margin-right:8px;height:10px;width:10px" src="${PHONE_ICON}" alt="">${COMPANY_PHONE_DISPLAY}
</a></div>
</div>
<div style="display:inline-block;width:100%;max-width:240px;float:right;padding:10px;margin-bottom:8px">
<div style="line-height:20px;font-weight:400;font-family:Arial;font-size:13px">General Phone Line:</div>
<div style="line-height:20px;font-weight:bold;font-size:15px">
<a style="text-decoration:none;font-family:Arial;color:rgb(0,0,0)" href="tel:+18555079770" target="_blank" rel="noopener">
<img style="margin-right:8px;height:10px;width:10px" src="${PHONE_ICON}" alt="">${GENERAL_LINE}
</a></div>
</div>
</td></tr>
<tr><td style="font-size:18px;line-height:22px;text-align:center;padding:0 0 8px">
<div style="width:100%"><div style="padding:10px">
<div style="line-height:20px;font-weight:400;font-family:Arial;font-size:13px">Email:</div>
<div style="line-height:20px;font-weight:bold;font-family:Arial;font-size:15px">
<a style="text-decoration:none;font-family:Arial;color:rgb(0,0,0)" href="mailto:${agentEmail}" target="_blank" rel="noopener">${agentEmail}</a>
</div></div></div>
</td></tr></tbody></table>`;
}
